import { Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConversationsContext } from '~/contexts/ConversationContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { deleteConversation, updateConversationTitle } from '~/services/conversationService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { Button } from '../common/Button'
import Typography from '../common/Typography'

export interface ChatItemProps {
  uuid: string
  title: string
  isSelected?: boolean
  onClick?: () => void
  className?: string
}

export const ChatItem: React.FC<ChatItemProps> = ({
  uuid,
  title,
  isSelected = false,
  onClick,
  className
}) => {
  const navigate = useNavigate()
  const { conversations, refetch, markConversationDeleted } = useConversationsContext()
  const { currentWorkspace } = useWorkspaceContext()
  const [isRenaming, setIsRenaming] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!currentWorkspace) {
      BetterToast.error('No workspace selected')
      return
    }

    if (isDeleting) return

    setIsDeleting(true)
    try {
      // Mark as deleted FIRST to prevent WebSocket events from trying to update it
      markConversationDeleted(uuid)

      await deleteConversation(currentWorkspace.uuid, uuid)
      setIsOpen(false)
      BetterToast.success('Conversation deleted successfully')

      // Refetch to get the updated list (deleted conversation removed)
      // This ensures we navigate to a conversation that actually exists
      const refetchResult = (await refetch()) as { data?: Array<{ uuid: string }> } | undefined
      const freshList = refetchResult?.data

      // Navigate if we're viewing the one being deleted
      if (isSelected) {
        if (freshList && freshList.length > 0) {
          // Navigate to another conversation
          navigate(`/w/${currentWorkspace.uuid}/c/${freshList[0].uuid}`, { replace: true })
        } else {
          // No conversations left - navigate to workspace home
          navigate(`/w/${currentWorkspace.uuid}`, { replace: true })
        }
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
      BetterToast.error('Failed to delete conversation')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    setNewTitle(title)
    setIsRenaming(true)
    setSelectedConversationId(uuid)
  }

  const handleSaveTitle = async () => {
    if (!selectedConversationId || !newTitle.trim()) return

    if (!currentWorkspace) {
      BetterToast.error('No workspace selected')
      return
    }

    if (isUpdating) return

    setIsUpdating(true)
    try {
      await updateConversationTitle({
        workspaceUuid: currentWorkspace.uuid,
        uuid: selectedConversationId,
        title: newTitle.trim()
      })
      await refetch()
      setIsRenaming(false)
      setNewTitle('')
      setSelectedConversationId(null)
      setIsOpen(false)
      BetterToast.success('Title updated successfully')
    } catch (error) {
      console.error('Failed to update title:', error)
      BetterToast.error('Failed to update title')
      setIsOpen(false)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div
      key={uuid}
      className={cm(
        'conversation select-none text-sm group relative rounded-[16px] hover:bg-tokens-status-link-base-2 active:bg-tokens-status-link-base-2',
        'p-4 cursor-pointer text-tokens-neutral-text-primary flex items-center justify-between',
        isSelected ? 'bg-tokens-status-link-base-2' : 'transparent',
        className
      )}
      onClick={onClick}
    >
      <div className='title flex-1 min-w-0 mr-2'>
        <AnimatePresence mode='wait'>
          <motion.div
            key={title} // This causes re-mount when title changes
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }}
            className={cm(
              'min-w-0 whitespace-nowrap text-ellipsis overflow-hidden font-medium text-sm leading-[22px] text-tokens-neutral-text-primary'
            )}
          >
            <Typography
              fontFamily='geist'
              fontSize='titleSm'
              fontWeight='semibold'
              className={cm(
                'truncate transition-colors duration-200',
                isSelected
                  ? 'text-[#65558F]'
                  : 'text-tokens-neutral-text-secondary group-hover:text-[#65558F]'
              )}
            >
              {title || 'Untitled'}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </div>

      <Popover
        placement='bottom-end'
        showArrow={false}
        isOpen={isOpen}
        onOpenChange={open => {
          setIsOpen(open)
          if (!open) {
            setIsRenaming(false)
            setNewTitle('')
            setSelectedConversationId(null)
          }
        }}
      >
        <PopoverTrigger>
          <Button
            variant='borderless'
            className='opacity-0 group-hover:opacity-100 min-w-[24px] w-[24px] h-[24px] p-0 flex items-center justify-center flex-shrink-0'
            onClick={e => e.stopPropagation()}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='text-tokens-neutral-text-secondary'
            >
              <path
                d='M7.125 3.60938C7.125 3.84144 7.21719 4.064 7.38128 4.22809C7.54538 4.39219 7.76794 4.48438 8 4.48438C8.23206 4.48438 8.45462 4.39219 8.61872 4.22809C8.78281 4.064 8.875 3.84144 8.875 3.60938C8.875 3.37731 8.78281 3.15475 8.61872 2.99066C8.45462 2.82656 8.23206 2.73438 8 2.73438C7.76794 2.73438 7.54538 2.82656 7.38128 2.99066C7.21719 3.15475 7.125 3.37731 7.125 3.60938ZM7.125 7.98438C7.125 8.21644 7.21719 8.439 7.38128 8.60309C7.54538 8.76719 7.76794 8.85938 8 8.85938C8.23206 8.85938 8.45462 8.76719 8.61872 8.60309C8.78281 8.439 8.875 8.21644 8.875 7.98438C8.875 7.75231 8.78281 7.52975 8.61872 7.36566C8.45462 7.20156 8.23206 7.10938 8 7.10938C7.76794 7.10938 7.54538 7.20156 7.38128 7.36566C7.21719 7.52975 7.125 7.75231 7.125 7.98438ZM7.125 12.3594C7.125 12.5914 7.21719 12.814 7.38128 12.9781C7.54538 13.1422 7.76794 13.2344 8 13.2344C8.23206 13.2344 8.45462 13.1422 8.61872 12.9781C8.78281 12.814 8.875 12.5914 8.875 12.3594C8.875 12.1273 8.78281 11.9048 8.61872 11.7407C8.45462 11.5766 8.23206 11.4844 8 11.4844C7.76794 11.4844 7.54538 11.5766 7.38128 11.7407C7.21719 11.9048 7.125 12.1273 7.125 12.3594Z'
                fill='currentColor'
              />
            </svg>
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className='p-2'>
            {isRenaming ? (
              <div className='flex flex-col gap-1.5'>
                <input
                  type='text'
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  onFocus={e => e.currentTarget.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSaveTitle()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setIsRenaming(false)
                    }
                  }}
                  className='border rounded-md px-2 py-1.5 w-full text-sm focus:outline-none'
                  placeholder='Enter new title'
                  autoFocus
                />
                <div className='flex justify-between gap-1.5'>
                  <Button
                    variant='outlined'
                    isDisabled={isUpdating}
                    onClick={() => setIsRenaming(false)}
                    className='w-[72px] h-7 text-xs'
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='primary'
                    isDisabled={isUpdating}
                    onClick={handleSaveTitle}
                    className='w-[72px] h-7 text-xs'
                  >
                    {isUpdating ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button
                  variant='borderless'
                  className='w-full justify-start mb-2 text-default-600'
                  onClick={handleRename}
                  isDisabled={isDeleting || isUpdating}
                >
                  Rename
                </Button>
                <Button
                  variant='borderless'
                  className='w-full justify-start text-danger'
                  onClick={handleDelete}
                  isDisabled={isDeleting || isUpdating}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default ChatItem
